import React, { useEffect, useState, ChangeEvent } from 'react';
import {
  Container,
  ContentHeader,
  Title,
  ActionToolbar,
  ContentSection,
  SectionTitle,
  FieldsGrid,
  DishesContainer,
  DishCard,
  DishImage,
  DishContent,
  DishName,
  DishDescription,
  DishMeta,
  DishPrice,
  DishActions,
  ContentBody,
  PaginationWrapper
} from './Menu.styled';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectCurrentRestaurant, selectMessage, setOpen, selectSeverity, selectShouldOpen, fetchRestaurantByAlias } from '../Restaurant/slice';
import PageContainer, { BreadCrumbLink } from '../../components/PageContainer/PageContainer';
import { useNavigate, useParams } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { publishMenu, updateMenu, deleteDish, deleteMenu } from './slice';
import { MenuRequest, Dish } from './types';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  fetchDishImageSuggestions,
  generateDishImageSuggestions,
  attachDishImageSuggestion,
  selectDishImageSuggestions,
  selectDishImageSuggestionsStatus,
  selectDishImageSuggestionsIsGenerating,
  selectDishImageSuggestionsGenerateError
} from './dishImageSuggestionsSlice';
import DishImageSuggestionModal from './DishImageSuggestionModal';

const ITEMS_PER_PAGE = 10;

const Menu: React.FC = () => {
  const { alias, menuID } = useParams();
  const restaurant = useAppSelector(selectCurrentRestaurant);
  const [menu, setMenu] = useState(restaurant?.menus?.find(m => m.menuID === menuID));
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const breadcrumbLinks: BreadCrumbLink[] = [
    {
      name: restaurant?.name ?? '',
      url: `/restaurant/${alias}`
    },
    {
      name: menu?.name ?? '',
      url: `/restaurant/${alias}/menu/${menuID}`
    }
  ];

  const [menuUrl, setMenuUrl] = useState(menu?.url ?? '');
  const [selectedDishId, setSelectedDishId] = useState<string | null>(null);
  const [isSuggestionModalOpen, setSuggestionModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingRestaurant, setIsLoadingRestaurant] = useState(false);

  // Log route params for debugging
  useEffect(() => {
    console.log('Menu route params', { alias, menuID });
    console.log('Current restaurant from store', {
      restaurantId: restaurant?.restaurantId,
      alias: restaurant?.alias,
      name: restaurant?.name,
      menusCount: restaurant?.menus?.length ?? 0
    });
  }, [alias, menuID, restaurant]);

  // Fetch restaurant by alias if missing or menu not found
  useEffect(() => {
    if (!alias) return;

    // If we don't have a restaurant loaded, or the menu for this menuID is missing,
    // trigger a fetch from the backend.
    const hasMenuOnRestaurant =
      restaurant?.menus?.some((m) => m.menuID === menuID) ?? false;

    if (!restaurant || !hasMenuOnRestaurant) {
      setIsLoadingRestaurant(true);
      void dispatch(fetchRestaurantByAlias(alias))
        .unwrap()
        .catch((err: unknown) => {
          console.error('Failed to load restaurant for menu page', err);
        })
        .finally(() => {
          setIsLoadingRestaurant(false);
        });
    }
    // We only depend on alias, menuID, restaurant reference, and dispatch here.
  }, [alias, menuID, restaurant, dispatch]);

  useEffect(() => {
    setMenu(restaurant?.menus?.find(m => m.menuID === menuID));
    setCurrentPage(1); // Reset to first page when menu changes
  }, [restaurant, menuID]);

  // Dish image suggestions
  const suggestions = useAppSelector(selectDishImageSuggestions);
  const suggestionsStatus = useAppSelector(selectDishImageSuggestionsStatus);
  const isGenerating = useAppSelector(selectDishImageSuggestionsIsGenerating);
  const generateError = useAppSelector(selectDishImageSuggestionsGenerateError);

  useEffect(() => {
    if (menu != null) {
      setMenuUrl(menu.url ?? '');
    }
  }, [menu]);

  // Note: We no longer load suggestions on mount - we load them lazily when modal opens

  const openServer = useAppSelector(selectShouldOpen);
  const message = useAppSelector(selectMessage);
  const severity = useAppSelector(selectSeverity);

  const setOpenServer = (open: boolean): void => {
    dispatch(setOpen(open));
  };

  const handleSaveMenu = (): void => {
    if (menu != null) {
      const mr: MenuRequest = {
        menuId: menu.menuID,
        name: menu.name,
        url: menuUrl ?? '',
        itemParser: '',
        titleParser: '',
        descriptionParser: '',
        priceParser: '',
        ingredientParser: '',
        dishes: menu.dishes
      };
      void dispatch(updateMenu(mr));
    }
  };

  const handlePublishMenu = (): void => {
    if (menu != null) {
      void dispatch(publishMenu(menu.menuID));
    }
  };

  const handleDeleteMenu = async (): Promise<void> => {
    if (menu == null || menuID == null || alias == null) {
      return;
    }

    const confirmed = window.confirm(
      'This will delete this menu and all of its dishes. Are you sure you want to continue?'
    );
    if (!confirmed) {
      return;
    }

    try {
      setIsDeleting(true);
      await dispatch(deleteMenu(menuID)).unwrap();
      // Navigate back to the restaurant page
      navigate(`/restaurant/${alias}`);
    } catch (error) {
      console.error('Failed to delete menu', error);
      // Error is handled via slice (status/message)
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRefresh = async (): Promise<void> => {
    if (!alias) return;
    try {
      setIsRefreshing(true);
      await dispatch(fetchRestaurantByAlias(alias)).unwrap();
    } catch (err) {
      console.error('Failed to refresh menu enrichment', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDeleteDish = (dishID: string): void => {
    void dispatch(deleteDish(dishID));
  };

  const handleEditDish = (dish: Dish): void => {
    navigate(`/restaurant/${alias}/menu/${menuID}/edit-dish/${dish.dishID}`, { state: { dish } });
  };

  const handleOpenSuggestionModal = async (dishId: string): Promise<void> => {
    if (restaurant?.restaurantId == null) {
      return;
    }

    setSelectedDishId(dishId);
    setSuggestionModalOpen(true);

    // Step 1: Fetch current suggestions
    try {
      const result = await dispatch(
        fetchDishImageSuggestions({ restaurantId: restaurant.restaurantId })
      ).unwrap();

      // If we already have suggestions, stop here
      if (result != null && result.length > 0) {
        return;
      }

      // Step 2: If empty, generate then refetch
      await dispatch(
        generateDishImageSuggestions({ restaurantId: restaurant.restaurantId, overwriteExisting: false })
      ).unwrap();

      // Step 3: Refetch after generation
      await dispatch(fetchDishImageSuggestions({ restaurantId: restaurant.restaurantId })).unwrap();
    } catch (err) {
      console.error('Failed to load or generate dish image suggestions', err);
      // The modal will show an error via generateError / status
    }
  };

  const handleCloseSuggestionModal = (): void => {
    setSuggestionModalOpen(false);
    setSelectedDishId(null);
  };

  const handleSuggestionSelected = async (suggestionId: string): Promise<void> => {
    if (restaurant?.restaurantId == null || selectedDishId == null) {
      return;
    }

    try {
      await dispatch(
        attachDishImageSuggestion({
          restaurantId: restaurant.restaurantId,
          dishId: selectedDishId,
          suggestionId,
          alias: alias ?? undefined
        })
      ).unwrap();

      // Refresh suggestions to mark this one as used
      await dispatch(fetchDishImageSuggestions({ restaurantId: restaurant.restaurantId })).unwrap();

      // Success message is handled by restaurant slice
      // Restaurant refresh is handled by attachDishImageSuggestion thunk
      handleCloseSuggestionModal();
    } catch (error) {
      console.error('Failed to attach suggested image:', error);
      // Error is already handled by Redux slice (toast notification)
    }
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number): void => {
    setCurrentPage(page);
  };

  // Calculate paginated dishes
  const totalDishes = menu?.dishes?.length ?? 0;
  const totalPages = Math.ceil(totalDishes / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedDishes = menu?.dishes?.slice(startIndex, endIndex) ?? [];

  // Guard against missing restaurant/menu data
  if (!restaurant && !isLoadingRestaurant) {
    return (
      <PageContainer title="Menu Details" breadcrumbs={breadcrumbLinks} open={openServer} setOpen={setOpenServer} message={message} severity={severity}>
        <Container>
          <ContentHeader>
            <Title>Unable to Load Menu</Title>
          </ContentHeader>
          <ContentBody>
            <Typography color="error" sx={{ mt: 2 }}>
              Unable to load this menu. The restaurant could not be found.
              {alias && (
                <>
                  {' '}Please go back to the{' '}
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate(`/restaurant/${alias}`)}
                    sx={{ textTransform: 'none' }}
                  >
                    restaurant page
                  </Button>
                  {' '}or the{' '}
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate('/')}
                    sx={{ textTransform: 'none' }}
                  >
                    dashboard
                  </Button>
                  .
                </>
              )}
            </Typography>
          </ContentBody>
        </Container>
      </PageContainer>
    );
  }

  if (!menu && !isLoadingRestaurant && restaurant) {
    return (
      <PageContainer title="Menu Details" breadcrumbs={breadcrumbLinks} open={openServer} setOpen={setOpenServer} message={message} severity={severity}>
        <Container>
          <ContentHeader>
            <Title>Menu Not Found</Title>
          </ContentHeader>
          <ContentBody>
            <Typography color="error" sx={{ mt: 2 }}>
              The requested menu could not be found for this restaurant.
              {alias && (
                <>
                  {' '}Please go back to the{' '}
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate(`/restaurant/${alias}`)}
                    sx={{ textTransform: 'none' }}
                  >
                    restaurant page
                  </Button>
                  .
                </>
              )}
            </Typography>
          </ContentBody>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer title={menu?.name} breadcrumbs={breadcrumbLinks} open={openServer} setOpen={setOpenServer} message={message} severity={severity}>
      <Container>
        {/* Header */}
        <ContentHeader>
          <Title>{menu?.name ?? 'Menu Details'}</Title>
          <ActionToolbar>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleRefresh}
              disabled={isRefreshing}
              startIcon={isRefreshing ? <CircularProgress size={16} /> : undefined}
            >
              {isRefreshing ? 'Refreshing…' : 'Refresh enrichment'}
            </Button>
            <Button variant="contained" size="small" onClick={handleSaveMenu}>
              Update
            </Button>
            <Button variant="outlined" size="small" onClick={handlePublishMenu}>
              Publish Menu
            </Button>
            <Button variant="outlined" size="small" onClick={() => {
              navigate(`/restaurant/${alias}/menu/${menuID}/add-dish`);
            }}>
              Add Dish
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              color="error"
              onClick={handleDeleteMenu}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting…' : 'Delete menu'}
            </Button>
          </ActionToolbar>
        </ContentHeader>

        <ContentBody>
          {isLoadingRestaurant && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading menu details…</Typography>
            </Box>
          )}
          {/* Menu Configuration */}
          <ContentSection>
            <SectionTitle>Menu Configuration</SectionTitle>
            <FieldsGrid>
              <TextField
                fullWidth
                label="Menu URL"
                variant="outlined"
                value={menuUrl}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setMenuUrl(event.target.value);
                }}
                size="small"
              />
            </FieldsGrid>
          </ContentSection>

          {/* Dishes */}
          {(menu?.dishes?.length ?? 0) > 0 && menu != null && (
            <ContentSection>
              <SectionTitle>Dishes ({menu.dishes.length})</SectionTitle>
              
              {/* Optional: Show banner if any dishes are pending enrichment */}
              {menu.dishes.some((dish) => dish.status === 'PENDING_ENRICHMENT') && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Some dishes are still being enriched. Predictions and advanced analytics may not reflect them yet.
                </Alert>
              )}
              
              <DishesContainer>
                {paginatedDishes.map((dish: Dish) => {
                  const isEnriched = dish.status === 'COMPLETED';
                  const isPending = dish.status === 'PENDING_ENRICHMENT';
                  
                  return (
                    <DishCard key={dish.dishID}>
                      {dish.imageUrl != null && (
                        <DishImage src={dish.imageUrl} alt={dish.name} />
                      )}
                      <DishContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <DishName>{dish.name}</DishName>
                          {isPending && (
                            <Chip
                              label="Enriching…"
                              color="warning"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                          {isEnriched && (
                            <Chip
                              label="Enriched"
                              color="success"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                        {dish.description != null && (
                          <DishDescription>{dish.description}</DishDescription>
                        )}
                        <DishMeta>
                          {dish.price != null && (
                            <DishPrice>${Number(dish.price).toFixed(2)}</DishPrice>
                          )}
                          {dish.preparation != null && (
                            <span>Prep: {dish.preparation}</span>
                          )}
                        </DishMeta>
                        
                        {/* Advanced fields - only show when enrichment is completed */}
                        {isEnriched ? (
                          <>
                            {dish.ingredients?.trim() && (
                              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                Ingredients: {dish.ingredients}
                              </Typography>
                            )}
                            {dish.dietary?.trim() && (
                              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                Dietary: {dish.dietary}
                              </Typography>
                            )}
                            {dish.unsuitabledietary?.trim() && (
                              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                Not suitable for: {dish.unsuitabledietary}
                              </Typography>
                            )}
                          </>
                        ) : isPending ? (
                          <Typography variant="body2" color="text.disabled" sx={{ mt: 1, fontStyle: 'italic' }}>
                            Enrichment in progress…
                          </Typography>
                        ) : null}
                      </DishContent>
                      <DishActions>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            handleEditDish(dish);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            // eslint-disable-next-line
                            handleOpenSuggestionModal(dish.dishID);
                          }}
                        >
                          Pick from suggestions
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            handleDeleteDish(dish.dishID);
                          }}
                        >
                          Delete
                        </Button>
                      </DishActions>
                    </DishCard>
                  );
                })}
              </DishesContainer>
              {totalPages > 1 && (
                <PaginationWrapper>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="medium"
                  />
                </PaginationWrapper>
              )}
            </ContentSection>
          )}
        </ContentBody>
      </Container>

      {/* Dish Image Suggestion Modal */}
      {restaurant?.restaurantId != null && (
        <DishImageSuggestionModal
          open={isSuggestionModalOpen}
          onClose={handleCloseSuggestionModal}
          restaurantId={restaurant.restaurantId}
          dishId={selectedDishId}
          suggestions={suggestions}
          status={suggestionsStatus}
          isGenerating={isGenerating}
          generateError={generateError}
          onSuggestionSelected={handleSuggestionSelected}
        />
      )}
    </PageContainer>
  );
};

export default withAuthenticationRequired(Menu);
